<script setup>
import {ref, watch} from "vue";

const props = defineProps({
    containerRef: {
        type: String,
        required: true
    },
    uuid: {
        type: String,
        required: true
    },
    content: {
        type: Object,
        required: true,
    }
})

const feed = ref(null);
const feedsLoaded = ref(false);
const currentPage = ref(1);
const lastPage = ref(1);
const pageBuilderActive = window.myflow.pagebuilderActive;

let activeLoadId = 0;

const loadFeeds = async (page = currentPage.value) => {
    const loadId = ++activeLoadId;

    feedsLoaded.value = false;

    try {
        const res = await axios.get(route('api.courses.feed.customerarea.multiple'), {
            params: {
                courses: JSON.stringify(props.content?.courses ?? []),
                per_page: props.content?.perPage,
                page,
            }
        });

        if (loadId !== activeLoadId) {
            return;
        }

        feed.value = res.data.success ? (res.data.feed ?? null) : null;
        currentPage.value = res.data.feed?.pagination?.current_page ?? page;
        lastPage.value = res.data.feed?.pagination?.last_page ?? 1;
    } catch (error) {
        if (loadId !== activeLoadId) {
            return;
        }

        feed.value = null;
        currentPage.value = 1;
        lastPage.value = 1;
    }

    feedsLoaded.value = true;
}

const changePage = (page) => {
    if (page < 1 || page > lastPage.value || page === currentPage.value) {
        return;
    }

    loadFeeds(page);
}

watch(
    () => props.content,
    () => {
        currentPage.value = 1;
        loadFeeds(1);
    },
    {deep: true, immediate: true}
);
</script>

<template>
    <div>
        <div v-if="feed">
            <ul :class="content.displayMode === 'grid' ? 'grid grid-cols-2 gap-4 mt-2' : ''">
                <li
                    v-if="feed.chapters.length === 0"
                    class="text-center"
                >
                    {{ _mft('course:feed.noContent') }}
                </li>
                <li
                    v-for="(chapter, i) in feed.chapters"
                    :key="chapter.id"
                    class="flex"
                    :class="content.displayMode === 'grid' ? 'flex flex-col rounded-wl overflow-hidden border bg-white/50' : 'py-2 mb-2 space-x-4 justify-between'"
                >
                    <div>
                        <component
                            :is="pageBuilderActive ? 'span' : 'a'"
                            :href="route('courses.feed.customerarea.chapter.show', [chapter.course_id, chapter.id])"
                            class="text-inherit cursor-pointer mf-link-immune"
                        >
                            <div
                                :class="content.displayMode === 'grid' ? 'p-2' : ''"
                                class="mf-link"
                            >
                                {{ chapter.title }}
                            </div>
                        </component>
                        <div
                            v-if="props.content.showAuthor || props.content.showDate"
                            class="text-xs opacity-60 cursor-default space-x-1"
                        >
                            <span v-if="props.content.showDate">
                                {{ chapter.published_at_user_tz_display }}
                            </span>
                            <span v-if="props.content.showDate && props.content.showAuthor">
                                {{ _mft('shared:by') }}
                            </span>
                            <span v-if="props.content.showAuthor">
                                {{ chapter.author_name }}
                            </span>
                        </div>

                        <div class="text-xs mt-1">{{ chapter.description }}</div>
                    </div>
                </li>
            </ul>
            <div
                v-if="lastPage > 1"
                class="mt-4 flex items-center justify-center gap-1"
            >
                <button
                    class="text-xs py-1 px-2"
                    :class="currentPage <= 1 ? 'opacity-40' : ''"
                    :disabled="currentPage <= 1"
                    @click="changePage(currentPage - 1)"
                >
                    <i class="fa-regular fa-arrow-left"></i>
                </button>
                <span class="text-sm opacity-60">
                    {{ currentPage }} / {{ lastPage }}
                </span>
                <button
                    class="text-xs py-1 px-2"
                    :class="currentPage >= lastPage ? 'opacity-40' : ''"
                    :disabled="currentPage >= lastPage"
                    @click="changePage(currentPage + 1)"
                >
                    <i class="fa-regular fa-arrow-right"></i>
                </button>
            </div>
        </div>
        <div v-else-if="feedsLoaded && !content.hideIfNotAvailable">
            <p>{{ content.courseNotAvailableMessage }}</p>
        </div>
    </div>
</template>

<style scoped>

</style>
